import React, { useState, useEffect } from 'react';
import NavigationSidebar from '../../components/navigation/NavigationSidebar';
import UserProfileDropdown from '../../components/navigation/UserProfileDropdown';
import NotificationCenter from '../../components/navigation/NotificationCenter';
import BreadcrumbNavigation from '../../components/navigation/BreadcrumbNavigation';
import MetricCard from './components/MetricCard';
import AgencyTable from './components/AgencyTable';
import RevenueChart from './components/RevenueChart';
import SystemAlerts from './components/SystemAlerts';
import SubscriptionBreakdown from './components/SubscriptionBreakdown';
import QuickActions from './components/QuickActions';
import FilterBar from './components/FilterBar';
import { useLanguage } from '../../contexts/LanguageContext';

const SuperAdminDashboard = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const { currentLanguage, changeLanguage, t } = useLanguage();
  const [filters, setFilters] = useState({
    search: '',
    subscription: 'all',
    status: 'all',
    dateFrom: '',
    dateTo: ''
  });

  const handleLanguageChange = (newLanguage) => {
    changeLanguage(newLanguage);
  };

  const mockUser = {
    name: currentLanguage === 'fr' ? 'Ahmed Benali' : 'أحمد بنعلي',
    role: 'super_admin',
    agency: 'OmraHajjManager',
    avatar: 'https://randomuser.me/api/portraits/men/45.jpg'
  };

  const mockNotifications = [
  {
    id: 1,
    title: t('notifications.newRegistration'),
    message: currentLanguage === 'fr' ? 'Agence Al-Baraka a créé un compte et attend validation' : 'وكالة البركة أنشأت حسابًا وتنتظر التحقق',
    timestamp: new Date(Date.now() - 1800000),
    priority: 'high',
    read: false
  },
  {
    id: 2,
    title: t('notifications.paymentReceived'),
    message: currentLanguage === 'fr' ? 'Agence Omra Express a renouvelé son abonnement Premium' : 'جددت وكالة عمرة إكسبرس اشتراكها المميز',
    timestamp: new Date(Date.now() - 7200000),
    priority: 'medium',
    read: false
  },
  {
    id: 3,
    title: t('notifications.systemAlert'),
    message: currentLanguage === 'fr' ? 'Utilisation du stockage à 85% - envisager une mise à niveau' : 'استخدام التخزين عند 85٪ - يرجى النظر في الترقية',
    timestamp: new Date(Date.now() - 14400000),
    priority: 'critical',
    read: true
  }];


  const mockMetrics = [
  {
    title: t('superAdmin.totalAgencies'),
    value: '247',
    change: '+12.5%',
    changeType: 'positive',
    icon: 'Building2',
    trend: [45, 52, 48, 61, 58, 67, 72]
  },
  {
    title: t('superAdmin.activeSubscriptions'),
    value: '189',
    change: '+8.3%',
    changeType: 'positive',
    icon: 'CheckCircle2',
    trend: [38, 42, 45, 48, 52, 55, 58]
  },
  {
    title: t('superAdmin.monthlyRevenue'),
    value: '1.2M MAD',
    change: '+15.7%',
    changeType: 'positive',
    icon: 'DollarSign',
    trend: [65, 72, 68, 78, 85, 92, 98]
  },
  {
    title: t('superAdmin.conversionRate'),
    value: '76.5%',
    change: '-2.1%',
    changeType: 'negative',
    icon: 'TrendingUp',
    trend: [82, 80, 78, 77, 76, 75, 76]
  }];


  const mockAgencies = [
  {
    id: 1,
    name: 'Omra Express',
    location: 'Casablanca',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_150e6744c-1781351839239.png",
    logoAlt: 'Modern office building with glass facade representing Omra Express travel agency headquarters in Casablanca Morocco',
    subscription: 'Plan Premium',
    status: 'active',
    pilgrimCount: 1247,
    revenue: '245,000 MAD',
    lastActivity: '2026-01-31T10:30:00'
  },
  {
    id: 2,
    name: 'Al-Baraka Voyages',
    location: 'Rabat',
    logo: "https://images.unsplash.com/photo-1681519488645-23b81f45a3b7",
    logoAlt: 'Traditional Moroccan architecture with ornate details representing Al-Baraka Voyages travel agency in Rabat Morocco',
    subscription: 'Plan Standard',
    status: 'active',
    pilgrimCount: 892,
    revenue: '178,000 MAD',
    lastActivity: '2026-01-31T09:15:00'
  },
  {
    id: 3,
    name: 'Hajj Services Pro',
    location: 'Marrakech',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_177ecc407-1767383843539.png",
    logoAlt: 'Professional business office interior with modern furniture representing Hajj Services Pro agency in Marrakech Morocco',
    subscription: 'Plan Premium',
    status: 'active',
    pilgrimCount: 1534,
    revenue: '312,000 MAD',
    lastActivity: '2026-01-31T14:20:00'
  },
  {
    id: 4,
    name: 'Omra Direct',
    location: 'Fès',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_131b748e8-1781356954620.png",
    logoAlt: 'Contemporary office workspace with computers and desks representing Omra Direct travel agency in Fès Morocco',
    subscription: 'Plan Basique',
    status: 'trial',
    pilgrimCount: 234,
    revenue: '45,000 MAD',
    lastActivity: '2026-01-30T16:45:00'
  },
  {
    id: 5,
    name: 'Makkah Tours',
    location: 'Tanger',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_151f28988-1781356953569.png",
    logoAlt: 'Bright modern office space with large windows representing Makkah Tours travel agency in Tanger Morocco',
    subscription: 'Plan Standard',
    status: 'suspended',
    pilgrimCount: 567,
    revenue: '98,000 MAD',
    lastActivity: '2026-01-28T11:30:00'
  }];


  const mockRevenueData = [
  { month: 'Jan', subscriptions: 85000, commissions: 12000 },
  { month: 'Fév', subscriptions: 92000, commissions: 15000 },
  { month: 'Mar', subscriptions: 88000, commissions: 13500 },
  { month: 'Avr', subscriptions: 105000, commissions: 18000 },
  { month: 'Mai', subscriptions: 118000, commissions: 22000 },
  { month: 'Juin', subscriptions: 125000, commissions: 25000 }];


  const mockSubscriptionData = [
  { name: 'Plan Basique', value: 78, percentage: 31.6 },
  { name: 'Plan Standard', value: 95, percentage: 38.5 },
  { name: 'Plan Premium', value: 58, percentage: 23.5 },
  { name: 'Essai gratuit', value: 16, percentage: 6.4 }];


  const mockAlerts = [
  {
    id: 1,
    title: 'Paiement en retard',
    message: 'Agence Omra Direct n\'a pas effectué le paiement mensuel depuis 5 jours',
    agency: 'Omra Direct',
    priority: 'critical',
    timestamp: new Date(Date.now() - 3600000)
  },
  {
    id: 2,
    title: 'Limite de stockage',
    message: 'Agence Hajj Services Pro a atteint 90% de sa limite de stockage',
    agency: 'Hajj Services Pro',
    priority: 'high',
    timestamp: new Date(Date.now() - 7200000)
  },
  {
    id: 3,
    title: 'Demande de support',
    message: 'Agence Al-Baraka Voyages a ouvert un ticket de support prioritaire',
    agency: 'Al-Baraka Voyages',
    priority: 'medium',
    timestamp: new Date(Date.now() - 10800000)
  }];


  const handleLogout = () => {
    console.log('Logout clicked');
  };

  const handleNotificationClick = (notification) => {
    console.log('Notification clicked:', notification);
  };

  const handleMarkAsRead = (notificationId) => {
    console.log('Mark as read:', notificationId);
  };

  const handleAgencyAction = (action, agencyId) => {
    console.log('Agency action:', action, agencyId);
  };

  const handleAlertAction = (action, alertId) => {
    console.log('Alert action:', action, alertId);
  };

  const handleQuickAction = (actionId) => {
    console.log('Quick action:', actionId);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      subscription: 'all',
      status: 'all',
      dateFrom: '',
      dateTo: ''
    });
  };

  const filteredAgencies = mockAgencies?.filter((agency) => {
    if (filters?.search && !agency?.name?.toLowerCase()?.includes(filters?.search?.toLowerCase())) {
      return false;
    }
    if (filters?.subscription !== 'all' && !agency?.subscription?.toLowerCase()?.includes(filters?.subscription)) {
      return false;
    }
    if (filters?.status !== 'all' && agency?.status !== filters?.status) {
      return false;
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-background">
      <NavigationSidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} />

      <div
        className={`transition-smooth ${
        isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-72'}`
        }>

        <header className="sticky top-0 z-30 bg-card border-b border-border shadow-warm">
          <div className="flex items-center justify-between pl-16 pr-4 md:px-6 lg:px-8 py-4">
            <div className="flex-1">
              <h1 className="text-xl md:text-2xl font-semibold text-foreground">
                {t('superAdmin.dashboardTitle')}
              </h1>
              <p className="caption text-muted-foreground mt-1">
                {currentLanguage === 'fr' ? 'Gestion complète de la plateforme et des agences' : 'إدارة كاملة للمنصة والوكالات'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <NotificationCenter
                notifications={mockNotifications}
                onNotificationClick={handleNotificationClick}
                onMarkAsRead={handleMarkAsRead}
                currentLanguage={currentLanguage} />

              <UserProfileDropdown
                user={mockUser}
                onLogout={handleLogout}
                onLanguageChange={handleLanguageChange}
                currentLanguage={currentLanguage} />

            </div>
          </div>
        </header>

        <main className="px-4 md:px-6 lg:px-8 py-6">
          <BreadcrumbNavigation currentLanguage={currentLanguage} />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6">
            {mockMetrics?.map((metric, index) =>
            <MetricCard key={index} {...metric} />
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <div className="lg:col-span-2">
              <RevenueChart data={mockRevenueData} currentLanguage={currentLanguage} />
            </div>
            <div>
              <SubscriptionBreakdown data={mockSubscriptionData} currentLanguage={currentLanguage} />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <div className="lg:col-span-2">
              <QuickActions onAction={handleQuickAction} currentLanguage={currentLanguage} />
            </div>
            <div>
              <SystemAlerts
                alerts={mockAlerts}
                onAlertAction={handleAlertAction}
                currentLanguage={currentLanguage} />

            </div>
          </div>

          <FilterBar
            filters={filters}
            onFilterChange={handleFilterChange}
            onReset={handleResetFilters}
            currentLanguage={currentLanguage} />


          <AgencyTable
            agencies={filteredAgencies}
            onAgencyAction={handleAgencyAction}
            currentLanguage={currentLanguage} />

        </main>

        <footer className="border-t border-border bg-card mt-12">
          <div className="px-4 md:px-6 lg:px-8 py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="caption text-muted-foreground text-center md:text-left">
                &copy; {new Date()?.getFullYear()} OmraHajjManager. Tous droits réservés.
              </p>
              <div className="flex items-center gap-4">
                <a href="#" className="caption text-muted-foreground hover:text-primary transition-smooth">
                  Politique de confidentialité
                </a>
                <a href="#" className="caption text-muted-foreground hover:text-primary transition-smooth">
                  Conditions d'utilisation
                </a>
                <a href="#" className="caption text-muted-foreground hover:text-primary transition-smooth">
                  Support
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>);

};

export default SuperAdminDashboard;